import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #motionSequence = 0;

  /** @type {'closed' | 'opening' | 'open' | 'closing'} */
  #motionState = 'closed';

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.closest('media-gallery')?.addEventListener('click', this.handleGalleryViewTransitionClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.closest('media-gallery')?.removeEventListener('click', this.handleGalleryViewTransitionClick);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    if (dialog.open || this.#motionState === 'opening' || this.#motionState === 'open') return;

    const motionSequence = ++this.#motionSequence;
    this.#motionState = 'opening';
    dialog.classList.remove('dialog--closed');

    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      if (motionSequence !== this.#motionSequence) return;

      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.#motionState = dialog.open ? 'open' : 'closed';
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    try {
      await startViewTransition(() => {
        open();
        removeViewTransitionName(sourceImage, itemTransitionName);
        targetImage.style.setProperty('view-transition-name', itemTransitionName);
      });
    } finally {
      removeViewTransitionName(sourceImage, itemTransitionName);
      removeViewTransitionName(targetImage, itemTransitionName);

      if (motionSequence === this.#motionSequence) {
        document.documentElement.style.removeProperty('--gallery-media-focal-point');
      }
    }

    if (motionSequence !== this.#motionSequence) return;

    this.selectThumbnail(index, { behavior: 'instant' });
    this.#motionState = dialog.open ? 'open' : 'closed';
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!dialog.open || this.#motionState === 'closing' || this.#motionState === 'closed') return;

    const motionSequence = ++this.#motionSequence;
    this.#motionState = 'closing';

    if (!supportsViewTransitions() || isLowPowerDevice()) {
      this.closeDialog();
      this.#motionState = 'closed';
      return;
    }

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);
    if (motionSequence !== this.#motionSequence || !dialog.open) return;

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) {
      this.closeDialog();
      this.#motionState = 'closed';
      return;
    }
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');
    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    try {
      await startViewTransition(() => {
        if (motionSequence !== this.#motionSequence) return;

        removeViewTransitionName(mostVisibleElement, itemTransitionName);
        slide.style.setProperty('view-transition-name', itemTransitionName);
        this.closeDialog();
      });
    } finally {
      removeViewTransitionName(mostVisibleElement, itemTransitionName);
      removeViewTransitionName(slide, itemTransitionName);

      if (motionSequence === this.#motionSequence) {
        dialog.classList.remove('dialog--closed');
        document.documentElement.style.removeProperty('--gallery-media-focal-point');
      }
    }

    if (motionSequence !== this.#motionSequence) return;
    this.#motionState = dialog.open ? 'open' : 'closed';
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Keeps visible zoom controls interactive while the View Transition API is
   * rendering snapshots. During that short phase Chrome hit-tests the live
   * dialog instead of its participating descendants.
   *
   * @param {MouseEvent} event
   */
  handleViewTransitionClick(event) {
    const { dialog } = this.refs;
    if (event.target !== dialog || !document.documentElement.matches(':active-view-transition')) return;

    const interactiveTargets = dialog.querySelectorAll(
      '.dialog-zoomed-gallery__close-button, .dialog-thumbnails-list__thumbnail, .product-media-container--image'
    );

    for (const target of interactiveTargets) {
      if (!(target instanceof HTMLElement) || !containsPoint(target, event.clientX, event.clientY)) continue;

      event.preventDefault();
      target.click();
      return;
    }
  }

  /**
   * Re-routes a click on the visible source-image snapshot while the closing
   * transition hides the corresponding live slide from hit testing.
   *
   * @param {MouseEvent} event
   */
  handleGalleryViewTransitionClick = (event) => {
    const { dialog } = this.refs;
    if (
      dialog.open ||
      this.#motionState !== 'closing' ||
      !document.documentElement.matches(':active-view-transition')
    ) {
      return;
    }

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('media-gallery'));
    if (!mediaGallery) return;

    const slideshowActive = mediaGallery.presentation === 'carousel';
    const sourceMedia = slideshowActive ? mediaGallery.slideshow?.slides : mediaGallery.media;
    if (!sourceMedia?.length) return;

    // A normal live-media click already has its own declarative zoom handler.
    if (event.target instanceof Element && sourceMedia.some((source) => source.contains(event.target))) return;

    const sourceIndex = sourceMedia.findIndex((source) => containsPoint(source, event.clientX, event.clientY));
    if (sourceIndex < 0) return;

    const source = sourceMedia[sourceIndex];
    const trigger = source.querySelector('.product-media-container__zoom-button') ?? source;
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();
    trigger.click();
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}

/**
 * Removes only the transition name owned by the current motion. A reversed
 * motion may already have assigned a different name to the same element.
 *
 * @param {HTMLElement} element
 * @param {string} transitionName
 */
function removeViewTransitionName(element, transitionName) {
  if (element.style.getPropertyValue('view-transition-name') === transitionName) {
    element.style.removeProperty('view-transition-name');
  }
}

/**
 * @param {HTMLElement} element
 * @param {number} x
 * @param {number} y
 * @returns {boolean}
 */
function containsPoint(element, x, y) {
  const rect = element.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}
